import { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { X, Download, CheckCircle } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { collection, addDoc, query, where, getDocs } from "firebase/firestore";
import db from "@/lib/firebaseDb";

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type FormValues = z.infer<typeof formSchema>;

export default function LeadMagnet() {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: "" },
  });

  useEffect(() => {
    // Don't show if already dismissed this session or already signed up
    const dismissed = sessionStorage.getItem("leadMagnetDismissed");
    const signedUp = localStorage.getItem("leadMagnetSignedUp");
    if (dismissed || signedUp) return;

    // Show after 30 seconds or 50% scroll
    const timer = setTimeout(() => setIsVisible(true), 30000);

    const handleScroll = () => {
      const scrollPercent = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent > 50) {
        setIsVisible(true);
        window.removeEventListener("scroll", handleScroll);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const dismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
    sessionStorage.setItem("leadMagnetDismissed", "true");
  };

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    try {
      const q = query(collection(db, "newsletter_subscribers"), where("email", "==", data.email));
      const existing = await getDocs(q);

      if (existing.empty) {
        await addDoc(collection(db, "newsletter_subscribers"), {
          email: data.email,
          subscribedAt: new Date().toISOString(),
          source: "lead-magnet-checklist",
        });
      }

      setIsSuccess(true);
      localStorage.setItem("leadMagnetSignedUp", "true");

      toast({
        title: "Check your inbox!",
        description: "Your free Turkey Travel Checklist is on its way.",
      });
    } catch (error) {
      console.error("Lead magnet error:", error);
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible || isDismissed) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-0 md:right-4 md:left-auto md:bottom-4">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 md:p-8 animate-in slide-in-from-bottom duration-300 border border-gray-200">
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {isSuccess ? (
          <div className="text-center py-4">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">You're All Set!</h3>
            <p className="text-gray-600 mb-4">
              Your free Turkey Travel Checklist has been sent. Check your packing checklist tool too!
            </p>
            <Button
              onClick={dismiss}
              variant="outline"
              className="text-[#E30A17] border-[#E30A17] hover:bg-[#E30A17] hover:text-white"
            >
              Continue Exploring
            </Button>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#E30A17]/10 mb-4">
                <Download className="h-7 w-7 text-[#E30A17]" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Free Turkey Travel Checklist</h3>
              <p className="text-gray-600 text-sm">
                Get our comprehensive packing and planning checklist delivered to your inbox. Everything you need for an amazing trip to Turkey.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm font-semibold mb-2">What's included:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-[#30D5C8] flex-shrink-0" />
                  Season-by-season packing list
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-[#30D5C8] flex-shrink-0" />
                  Pre-trip document checklist
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-[#30D5C8] flex-shrink-0" />
                  Essential Turkish phrases
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-[#30D5C8] flex-shrink-0" />
                  Budget planning template
                </li>
              </ul>
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Enter your email"
                          aria-label="Email address for free checklist"
                          className="py-3"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full bg-[#E30A17] hover:bg-[#c00914] text-white font-semibold py-3"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Get Free Checklist"}
                </Button>
              </form>
            </Form>

            <p className="text-xs text-gray-400 text-center mt-3">
              No spam, ever. Unsubscribe anytime.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
